import { shallowRef, ref, readonly } from 'vue'
import type { DataKey } from './source'
import { loadData } from './source'
export interface UseJsonResourceOptions<T> {
  immediate?: boolean
  map?: (value: unknown, fallback: T) => T
  request?: RequestInit & { timeoutMs?: number }
}

export function useJsonResource<T>(
  key: DataKey,
  fallback: T,
  options?: UseJsonResourceOptions<T>
) {
  const data = shallowRef<T>(fallback)
  const loading = ref(false)
  const error = ref<Error | null>(null)
  let inFlight: Promise<void> | null = null

  const fetchData = async (force = false) => {
    if (inFlight && !force) return inFlight
    loading.value = true
    error.value = null

    const run = async () => {
      try {
        const response = await loadData(key, { ...(options?.request ?? {}), force })
        const mapped = options?.map ? options.map(response, data.value) : (response as T)
        data.value = mapped ?? fallback
      } catch (err) {
        error.value = err instanceof Error ? err : new Error(String(err))
        if (force) data.value = fallback
      } finally {
        loading.value = false
      }
    }

    inFlight = run()
    try {
      await inFlight
    } finally {
      inFlight = null
    }
  }

  if (options?.immediate !== false) {
    fetchData().catch(() => {})
  }

  const refresh = (force = true) => fetchData(force)

  return {
    data: readonly(data),
    loading: readonly(loading),
    error: readonly(error),
    refresh
  }
}
